<script lang="ts">
    /**
     * ProductCard - 회사 허브의 프로덕트 카드 (재사용)
     *
     * JadEye / Gridverse 등 회사 프로덕트를 동일 패턴으로 소개.
     * accent 컬러만 바꿔 프로덕트별 정체성 부여 (jade 톤 팔레트 내).
     */
    interface Props {
        name: string;
        koName?: string;
        tagline: string;
        description: string;
        icon: string; // 이모지 또는 이미지 경로
        iconIsImage?: boolean;
        accent?: string; // 액센트 컬러 (hex)
        badge?: string; // 예: "Coming Soon"
        href: string;
        ctaLabel?: string;
        features?: string[];
    }

    let {
        name,
        koName = '',
        tagline,
        description,
        icon,
        iconIsImage = false,
        accent = '#1E5C3A',
        badge = '',
        href,
        ctaLabel = '자세히 보기',
        features = [],
    }: Props = $props();
</script>

<a class="product-card" {href} style="--card-accent: {accent};">
    <div class="card-head">
        <div class="card-icon">
            {#if iconIsImage}
                <img src={icon} alt="{name} 로고" class="card-icon-img" />
            {:else}
                <span class="card-emoji">{icon}</span>
            {/if}
        </div>
        {#if badge}
            <span class="card-badge">{badge}</span>
        {/if}
    </div>

    <div class="card-body">
        <div class="card-titles">
            <h3 class="card-name">{name}</h3>
            {#if koName}<span class="card-koname">{koName}</span>{/if}
        </div>
        <p class="card-tagline">{tagline}</p>
        <p class="card-desc">{description}</p>

        {#if features.length}
            <ul class="card-features">
                {#each features as f}
                    <li>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path d="M20 6 9 17l-5-5" />
                        </svg>
                        {f}
                    </li>
                {/each}
            </ul>
        {/if}
    </div>

    <div class="card-cta">
        <span>{ctaLabel}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
        </svg>
    </div>
</a>

<style>
    .product-card {
        display: flex;
        flex-direction: column;
        background: var(--landing-bg, #ffffff);
        border: 1px solid rgba(0, 0, 0, 0.06);
        border-radius: 24px;
        padding: 2rem;
        text-decoration: none;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
        transition:
            transform 0.3s cubic-bezier(0.16, 1, 0.3, 1),
            box-shadow 0.3s ease,
            border-color 0.3s ease;
        position: relative;
        overflow: hidden;
    }

    /* 상단 액센트 라인 */
    .product-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: var(--card-accent, #1e5c3a);
        opacity: 0.85;
    }

    .product-card:hover {
        transform: translateY(-6px);
        box-shadow: 0 16px 40px rgba(0, 0, 0, 0.1);
        border-color: color-mix(in srgb, var(--card-accent, #1e5c3a) 35%, transparent);
    }

    /* Head: 아이콘 + 배지 */
    .card-head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        margin-bottom: 1.25rem;
    }

    .card-icon {
        width: 3.5rem;
        height: 3.5rem;
        border-radius: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: color-mix(in srgb, var(--card-accent, #1e5c3a) 10%, transparent);
    }

    .card-emoji {
        font-size: 1.85rem;
        line-height: 1;
    }

    .card-icon-img {
        width: 2.25rem;
        height: 2.25rem;
        object-fit: contain;
    }

    .card-badge {
        display: inline-flex;
        align-items: center;
        padding: 0.3rem 0.7rem;
        border-radius: 999px;
        font-size: 0.72rem;
        font-weight: 600;
        letter-spacing: 0.02em;
        color: var(--card-accent, #1e5c3a);
        background: color-mix(in srgb, var(--card-accent, #1e5c3a) 12%, transparent);
    }

    /* Body */
    .card-body {
        flex: 1;
    }

    .card-titles {
        display: flex;
        align-items: baseline;
        gap: 0.5rem;
        margin-bottom: 0.4rem;
    }

    .card-name {
        font-size: 1.5rem;
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--landing-text, #1a1a1a);
        margin: 0;
    }

    .card-koname {
        font-size: 0.9rem;
        font-weight: 500;
        color: var(--landing-text-sub, #666666);
    }

    .card-tagline {
        font-size: 1.02rem;
        font-weight: 600;
        color: var(--card-accent, #1e5c3a);
        margin: 0 0 0.6rem;
    }

    .card-desc {
        font-size: 0.95rem;
        line-height: 1.65;
        color: var(--landing-text-sub, #666666);
        margin: 0 0 1.25rem;
    }

    .card-features {
        list-style: none;
        padding: 0;
        margin: 0 0 1.5rem;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .card-features li {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.9rem;
        color: var(--landing-text, #1a1a1a);
    }

    .card-features svg {
        color: var(--card-accent, #1e5c3a);
        flex-shrink: 0;
    }

    /* CTA */
    .card-cta {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        font-size: 0.95rem;
        font-weight: 600;
        color: var(--card-accent, #1e5c3a);
        transition: gap 0.2s ease;
    }

    .product-card:hover .card-cta {
        gap: 0.65rem;
    }

    @media (min-width: 768px) {
        .product-card {
            padding: 2.5rem;
        }
    }
</style>
